/* =============================================================================
 * Product Reviews (czkr94ut)
 *
 * SSR renders the summary + first page from Shopify metafields. This runtime:
 *
 *   1. Hydrates state from the seed JSON blob emitted by the Liquid template.
 *   2. Handles pagination (page 2+ fetched from the app proxy API).
 *   3. Sort changes trigger a fresh API fetch and re-render.
 *   4. Rating filter (click histogram bar) narrows the list.
 *   5. Voting (helpful / not helpful) via POST to API.
 *   6. Review submission form with star rating input.
 *   7. Analytics via __spectrumAi.snippet.bind().
 *
 * No-bind fallback: if __spectrumAi is absent, SSR content remains usable.
 * Sort, pagination, and form features degrade (hidden or noop) since they
 * require the app proxy. Analytics silently skipped.
 * ============================================================================= */

;(() => {
  if (window.__sai_czkr94ut_initialized__) return
  window.__sai_czkr94ut_initialized__ = true

  const SNIPPET_ID = 'czkr94ut'
  const TAG = 'sai-czkr94ut'
  const FEATURE_SLUG = 'reviews'

  function noopTrack() {}
  function safeTrack(track) {
    return (name, payload) => {
      try {
        track(name, payload)
      } catch (_) {
        /* analytics is best-effort */
      }
    }
  }

  function formatDate(iso) {
    if (!iso) return ''
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return ''
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    const dd = String(d.getDate()).padStart(2, '0')
    const yyyy = d.getFullYear()
    return `${mm}/${dd}/${yyyy}`
  }

  function starsSvg(rating, size) {
    const sz = size || 16
    let html = ''
    for (let i = 1; i <= 5; i++) {
      if (i <= rating) {
        html += `<svg class="sai-czkr94ut__star-sm sai-czkr94ut__star-sm--full" width="${sz}" height="${sz}" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.27 5.82 22 7 14.14l-5-4.87 6.91-1.01L12 2z"/></svg>`
      } else {
        html += `<svg class="sai-czkr94ut__star-sm sai-czkr94ut__star-sm--empty" width="${sz}" height="${sz}" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.27 5.82 22 7 14.14l-5-4.87 6.91-1.01L12 2z"/></svg>`
      }
    }
    return html
  }

  function buildReviewCard(r, opts) {
    const rating = Math.round(Number(r.rating) || 0)
    const title = r.title || ''
    const body = r.body || ''
    const author = r.authorName || 'Anonymous'
    const date = formatDate(r.reviewedAt)
    const verified = r.verificationStatus === 'verified_buyer'
    const reply = r.reply
    const votes = r.voteCounts || {}
    const currentVote = r.currentCustomerVote || null
    const showVoting = opts?.showVoting !== false

    let html = `<article class="sai-czkr94ut__review" data-review-id="${r.id}">`

    html += `<div class="sai-czkr94ut__review-aside">`
    html += `<span class="sai-czkr94ut__review-author">${author}</span>`
    if (verified)
      html += `<span class="sai-czkr94ut__badge sai-czkr94ut__badge--verified"><svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg> Verified</span>`
    if (date) html += `<time class="sai-czkr94ut__review-date">${date}</time>`
    html += '</div>'

    html += `<div class="sai-czkr94ut__review-main">`
    html += `<div class="sai-czkr94ut__review-stars" aria-label="${rating} out of 5 stars">${starsSvg(rating)}</div>`
    if (title) html += `<h3 class="sai-czkr94ut__review-title">${title}</h3>`
    if (body) html += `<div class="sai-czkr94ut__review-body">${body}</div>`

    const media = Array.isArray(r.media) ? r.media.filter((m) => m.url) : []
    if (media.length > 0) {
      const reviewAttrs = ` data-review-rating="${rating}" data-review-author="${author}" data-review-title="${title}" data-review-body="${body}" data-review-date="${r.reviewedAt || ''}" data-review-verified="${r.verificationStatus || ''}"`
      html += `<div class="sai-czkr94ut__media">`
      for (const m of media) {
        if (m.mediaType === 'video') {
          html += `<button class="sai-czkr94ut__media-thumb sai-czkr94ut__media-thumb--video" type="button" data-action="media-open" data-media-url="${m.url}" data-media-type="video"${reviewAttrs} aria-label="Play video"><video src="${m.url}" preload="metadata" muted playsinline></video><span class="sai-czkr94ut__media-play"><svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg></span></button>`
        } else {
          html += `<button class="sai-czkr94ut__media-thumb" type="button" data-action="media-open" data-media-url="${m.url}" data-media-type="image"${reviewAttrs} aria-label="View image"><img src="${m.url}" alt="" loading="lazy" onerror="this.parentElement.style.display='none'"></button>`
        }
      }
      html += '</div>'
    }

    const isLoggedIn = !!window.__spectrumAi?.customer
    if (showVoting && isLoggedIn) {
      html += `<div class="sai-czkr94ut__votes">`
      html += `<span class="sai-czkr94ut__votes-label">Helpful?</span>`
      const helpfulCount = votes.helpful || 0
      const notHelpfulCount = votes.not_helpful || 0
      html += `<button class="sai-czkr94ut__vote-btn${currentVote === 'helpful' ? ' sai-czkr94ut__vote-btn--active' : ''}" type="button" data-action="vote" data-vote-type="helpful" data-review-id="${r.id}"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M14 9V5a3 3 0 00-3-3l-4 9v11h11.28a2 2 0 002-1.7l1.38-9a2 2 0 00-2-2.3H14z"/><path d="M7 22H4a2 2 0 01-2-2v-7a2 2 0 012-2h3"/></svg><span class="sai-czkr94ut__vote-divider">|</span><span class="sai-czkr94ut__vote-count">${helpfulCount}</span></button>`
      html += `<button class="sai-czkr94ut__vote-btn${currentVote === 'not_helpful' ? ' sai-czkr94ut__vote-btn--active' : ''}" type="button" data-action="vote" data-vote-type="not_helpful" data-review-id="${r.id}"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M10 15v4a3 3 0 003 3l4-9V2H5.72a2 2 0 00-2 1.7l-1.38 9a2 2 0 002 2.3H10z"/><path d="M17 2h2.67A2.31 2.31 0 0122 4v7a2.31 2.31 0 01-2.33 2H17"/></svg><span class="sai-czkr94ut__vote-divider">|</span><span class="sai-czkr94ut__vote-count">${notHelpfulCount}</span></button>`
      html += '</div>'
    }

    if (reply) {
      html += `<div class="sai-czkr94ut__reply">`
      html += `<span class="sai-czkr94ut__reply-label">${opts?.replyLabel || 'Store Reply'}</span>`
      if (reply.createdAt)
        html += `<time class="sai-czkr94ut__reply-date">${formatDate(reply.createdAt)}</time>`
      html += `<div class="sai-czkr94ut__reply-body">${reply.body}</div>`
      html += '</div>'
    }

    html += '</div>'
    html += '</article>'
    return html
  }

  function buildShimmer(count) {
    let html = '<div class="sai-czkr94ut__shimmer">'
    for (let i = 0; i < count; i++) {
      html += '<div class="sai-czkr94ut__shimmer-card">'
      html += '<div class="sai-czkr94ut__shimmer-aside">'
      html += '<div class="sai-czkr94ut__shimmer-line sai-czkr94ut__shimmer-line--short"></div>'
      html += '<div class="sai-czkr94ut__shimmer-line sai-czkr94ut__shimmer-line--tiny"></div>'
      html += '</div>'
      html += '<div class="sai-czkr94ut__shimmer-main">'
      html += '<div class="sai-czkr94ut__shimmer-line sai-czkr94ut__shimmer-line--stars"></div>'
      html += '<div class="sai-czkr94ut__shimmer-line sai-czkr94ut__shimmer-line--medium"></div>'
      html += '<div class="sai-czkr94ut__shimmer-line sai-czkr94ut__shimmer-line--long"></div>'
      html += '<div class="sai-czkr94ut__shimmer-line sai-czkr94ut__shimmer-line--long"></div>'
      html += '</div>'
      html += '</div>'
    }
    html += '</div>'
    return html
  }

  if (!customElements.get(TAG)) {
    class SaiReviews extends HTMLElement {
      connectedCallback() {
        if (this._initialized) return
        this._initialized = true
        this._track = noopTrack
        this._emit = noopTrack
        this._page = 1
        this._sortBy = 'newest'
        this._ratingFilter = null
        this._inflight = false
        this._paginationType = this.getAttribute('data-pagination-type') || 'numbered'
        this._totalPages = 1
        this._allLoadedReviews = []

        this._showVoting = this.getAttribute('data-show-voting') !== 'false'

        this._hydrateSeed()
        this._updatePagination(this._page, this._totalPages)
        this._updateLoadMore(this._page, this._totalPages)
        this._initSort()
        this._initHistogramFilter()
        this._initPagination()
        this._initLoadMore()
        this._initInfiniteScroll()
        this._initWriteReviewLink()
        this._initVoting()
        this._initReadMore()
        this._initMediaLightbox()
        this._hydrateVoteCounts()
      }

      setAnalytics(track, emit) {
        this._track = typeof track === 'function' ? safeTrack(track) : noopTrack
        this._emit = typeof emit === 'function' ? safeTrack(emit) : noopTrack
      }

      _hydrateSeed() {
        const container = this.closest('[data-spectrum-instance-id]') || this.parentElement
        const seedEl = container ? container.querySelector('script[data-sai-czkr94ut-seed]') : null
        if (!seedEl) return
        try {
          const seed = JSON.parse(seedEl.textContent)
          this._stats = seed.stats || {}
          this._config = seed.config || {}
          this._perPage = seed.perPage || 10
          this._sortBy =
            this.getAttribute('data-default-sort') || this._config.defaultStorefrontSort || 'newest'
          const totalCount = Number(this.getAttribute('data-total-count')) || 0
          this._totalPages = this._perPage > 0 ? Math.ceil(totalCount / this._perPage) : 1
          const sortSelect = this.querySelector('[data-action="sort"]')
          if (sortSelect) sortSelect.value = this._sortBy
        } catch (_) {
          /* seed parse failure — SSR state is sufficient */
        }
      }

      _getProductHandle() {
        return this.getAttribute('data-product-handle') || ''
      }

      async _hydrateVoteCounts() {
        if (!window.__spectrumAi?.customer) return
        this._ensureProxyBase()
        const sdk = window.Spectrum?.reviews
        if (!sdk?.getReviews) return
        try {
          const result = await sdk.getReviews(this._getProductHandle(), {
            page: 1,
            limit: this._perPage || 10,
            sortBy: this._sortBy,
          })
          if (!result.ok) return
          const data = result.data?.data || result.data
          const reviews = data?.reviews || []
          for (const r of reviews) {
            if (!r.id || !r.voteCounts) continue
            const cards = this.querySelectorAll(
              `[data-review-id*="${r.id}"], [data-review-id="spectrum-review-${r.id}"]`,
            )
            for (const card of cards) {
              const review = card.closest('.sai-czkr94ut__review') || card
              const voteBtns = review.querySelectorAll('[data-action="vote"]')
              for (const vb of voteBtns) {
                const type = vb.getAttribute('data-vote-type')
                const count = r.voteCounts[type] || 0
                const countEl = vb.querySelector('.sai-czkr94ut__vote-count')
                if (countEl) countEl.textContent = count
                vb.classList.toggle(
                  'sai-czkr94ut__vote-btn--active',
                  r.currentCustomerVote === type,
                )
              }
            }
          }
        } catch (_) {
          /* best-effort hydration */
        }
      }

      _ensureProxyBase() {
        if (window.__spectrumAi?.baseProxyUrl) return
        if (!window.__spectrumAi) window.__spectrumAi = {}
        const configEl = document.querySelector('script[data-spectrum-config]')
        if (configEl) {
          try {
            const cfg = JSON.parse(configEl.textContent)
            if (cfg.baseProxyUrl) {
              window.__spectrumAi.baseProxyUrl = cfg.baseProxyUrl
              return
            }
          } catch (_) {
            /* fall through */
          }
        }
        const org = document.querySelector('[data-spectrum-org]')?.getAttribute('data-spectrum-org')
        window.__spectrumAi.baseProxyUrl = org ? `/apps/spectrum_ai_${org}` : '/apps/spectrum'
      }

      async _fetchReviews(page, sortBy, ratingFilter) {
        this._ensureProxyBase()
        const sdk = window.Spectrum?.reviews
        if (!sdk) throw new Error('Spectrum SDK not available')
        const opts = { page, limit: this._perPage, sortBy }
        if (ratingFilter) opts.rating = ratingFilter
        const result = await sdk.getReviews(this._getProductHandle(), opts)
        if (!result.ok) throw new Error(result.error?.message || 'Failed to fetch reviews')
        const body = result.data
        return body.data || body
      }

      async _loadPage(page, sortBy, ratingFilter, opts) {
        if (this._inflight) return
        this._inflight = true
        const append = opts?.append === true

        const listEl = this.querySelector('[data-slot="reviews"]')
        if (!listEl) {
          this._inflight = false
          return
        }

        const loadMoreBtn = this.querySelector('[data-action="load-more"]')
        if (!append) {
          if (this._paginationType === 'numbered' && typeof this.scrollIntoView === 'function') {
            this.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
          listEl.innerHTML = buildShimmer(3)
        } else if (loadMoreBtn) {
          loadMoreBtn.disabled = true
          loadMoreBtn.textContent = 'Loading...'
        }
        if (append && this._paginationType === 'infinite') {
          this._showInfiniteSpinner(true)
        }

        try {
          const data = await this._fetchReviews(page, sortBy, ratingFilter)

          this._page = data.pagination.page
          this._totalPages = data.pagination.totalPages
          const totalCount = data.pagination.totalCount

          const cardOpts = {
            showVoting: this._showVoting,
            replyLabel: this.getAttribute('data-reply-label') || 'Store Reply',
          }
          const html = data.reviews.map((r) => buildReviewCard(r, cardOpts)).join('')

          if (append) {
            listEl.insertAdjacentHTML('beforeend', html)
          } else if (data.reviews.length === 0) {
            const emptyText = this.getAttribute('data-empty-text') || 'No reviews yet.'
            listEl.innerHTML = `<p class="sai-czkr94ut__empty">${ratingFilter ? 'No reviews match this filter.' : emptyText}</p>`
          } else {
            listEl.innerHTML = html
          }

          this._updatePagination(this._page, this._totalPages)
          this._updateLoadMore(this._page, this._totalPages)
          this._attachReadMoreButtons(listEl)

          const countEl = this.querySelector('.sai-czkr94ut__review-count')
          if (countEl) countEl.textContent = `Reviews (${totalCount})`

          this._track(`${FEATURE_SLUG}:page_view`, {
            page: this._page,
            sort_by: sortBy,
            rating_filter: ratingFilter || null,
            result_count: data.reviews.length,
          })
        } catch (err) {
          if (!append) {
            listEl.innerHTML = `<p class="sai-czkr94ut__empty">Failed to load reviews. Please try again.</p>`
          }
        }

        if (loadMoreBtn) {
          loadMoreBtn.disabled = false
          loadMoreBtn.textContent = 'Load More Reviews'
        }
        this._showInfiniteSpinner(false)
        this._inflight = false
      }

      _updatePagination(page, totalPages) {
        const paginationEl = this.querySelector('[data-slot="pagination"]')
        if (!paginationEl) return

        if (this._paginationType !== 'numbered' || totalPages <= 1) {
          paginationEl.hidden = true
          return
        }

        paginationEl.hidden = false
        const prevBtn = paginationEl.querySelector('[data-action="prev-page"]')
        const nextBtn = paginationEl.querySelector('[data-action="next-page"]')
        const infoEl = paginationEl.querySelector('[data-slot="page-info"]')

        if (prevBtn) prevBtn.disabled = page <= 1
        if (nextBtn) nextBtn.disabled = page >= totalPages
        if (infoEl) infoEl.textContent = `Page ${page} of ${totalPages}`
      }

      _updateLoadMore(page, totalPages) {
        const loadMoreEl = this.querySelector('[data-slot="load-more"]')
        if (!loadMoreEl) return
        loadMoreEl.hidden = this._paginationType !== 'load_more' || page >= totalPages
      }

      _initLoadMore() {
        const loadMoreEl = this.querySelector('[data-slot="load-more"]')
        if (!loadMoreEl) return
        loadMoreEl.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-action="load-more"]')
          if (!btn || this._inflight) return
          this._loadPage(this._page + 1, this._sortBy, this._ratingFilter, { append: true })
        })
      }

      _initInfiniteScroll() {
        if (this._paginationType !== 'infinite') return
        const sentinel = document.createElement('div')
        sentinel.className = 'sai-czkr94ut__scroll-sentinel'
        const listEl = this.querySelector('[data-slot="reviews"]')
        if (!listEl) return
        listEl.after(sentinel)

        if (typeof IntersectionObserver === 'undefined') return
        const observer = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (entry.isIntersecting && !this._inflight && this._page < this._totalPages) {
                this._loadPage(this._page + 1, this._sortBy, this._ratingFilter, {
                  append: true,
                }).finally(() => {
                  observer.unobserve(sentinel)
                  observer.observe(sentinel)
                })
              }
            }
          },
          { rootMargin: '200px' },
        )
        observer.observe(sentinel)
      }

      _showInfiniteSpinner(show) {
        const sentinel = this.querySelector('.sai-czkr94ut__scroll-sentinel')
        if (!sentinel) return
        if (show) {
          sentinel.innerHTML = buildShimmer(1)
        } else {
          sentinel.innerHTML = ''
        }
      }

      _initReadMore() {
        this._attachReadMoreButtons(this)
        this.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-action="read-more"]')
          if (!btn) return
          const body = btn.previousElementSibling
          if (!body?.classList.contains('sai-czkr94ut__review-body')) return
          const expanded = body.classList.toggle('sai-czkr94ut__review-body--expanded')
          btn.textContent = expanded ? 'Show less' : 'Read more'
        })
      }

      _attachReadMoreButtons(container) {
        const bodies = container.querySelectorAll('.sai-czkr94ut__review-body')
        for (const body of bodies) {
          if (body.nextElementSibling?.getAttribute('data-action') === 'read-more') continue
          if (body.scrollHeight <= body.clientHeight) continue
          const btn = document.createElement('button')
          btn.className = 'sai-czkr94ut__read-more'
          btn.type = 'button'
          btn.setAttribute('data-action', 'read-more')
          btn.textContent = 'Read more'
          body.after(btn)
        }
      }

      _initSort() {
        const sortSelect = this.querySelector('[data-action="sort"]')
        if (!sortSelect) return
        sortSelect.addEventListener('change', () => {
          this._sortBy = sortSelect.value
          this._page = 1
          this._loadPage(1, this._sortBy, this._ratingFilter)
          this._track(`${FEATURE_SLUG}:sort`, { sort_by: this._sortBy })
        })
      }

      _initHistogramFilter() {
        const histogram = this.querySelector('.sai-czkr94ut__histogram')
        if (!histogram) return
        histogram.addEventListener('click', (e) => {
          const row = e.target.closest('[data-rating-filter]')
          if (!row) return
          if (Number(row.getAttribute('data-count')) === 0) return
          const rating = Number(row.getAttribute('data-rating-filter'))
          if (this._ratingFilter === rating) {
            // Toggle off
            this._ratingFilter = null
            this._removeFilterIndicator()
          } else {
            this._ratingFilter = rating
            this._showFilterIndicator(rating)
          }
          this._page = 1
          this._loadPage(1, this._sortBy, this._ratingFilter)
          this._track(`${FEATURE_SLUG}:filter`, { rating_filter: this._ratingFilter })
        })
      }

      _showFilterIndicator(rating) {
        this._removeFilterIndicator()
        const controls = this.querySelector('.sai-czkr94ut__controls')
        if (!controls) return
        const indicator = document.createElement('div')
        indicator.className = 'sai-czkr94ut__active-filter'
        indicator.innerHTML = `<span>Showing ${rating}-star reviews</span><button class="sai-czkr94ut__clear-filter" type="button">Clear</button>`
        indicator.querySelector('.sai-czkr94ut__clear-filter').addEventListener('click', () => {
          this._ratingFilter = null
          this._removeFilterIndicator()
          this._page = 1
          this._loadPage(1, this._sortBy, null)
        })
        controls.after(indicator)
      }

      _removeFilterIndicator() {
        const existing = this.querySelector('.sai-czkr94ut__active-filter')
        if (existing) existing.remove()
      }

      _initPagination() {
        const pagination = this.querySelector('[data-slot="pagination"]')
        if (!pagination) return
        pagination.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-action]')
          if (!btn || btn.disabled) return
          const action = btn.getAttribute('data-action')
          if (action === 'prev-page' && this._page > 1) {
            this._loadPage(this._page - 1, this._sortBy, this._ratingFilter)
          } else if (action === 'next-page') {
            this._loadPage(this._page + 1, this._sortBy, this._ratingFilter)
          }
        })
      }

      _initWriteReviewLink() {
        const link = this.querySelector('[data-action="write-review"]')
        if (!link) return
        link.addEventListener('click', () => {
          this._track(`${FEATURE_SLUG}:write_review_click`, {})
        })
      }

      _initMediaLightbox() {
        if (this._mediaBound) return
        this._mediaBound = true

        const dialog = document.createElement('dialog')
        dialog.className = 'sai-czkr94ut__lightbox'
        dialog.setAttribute('aria-modal', 'true')
        dialog.setAttribute('aria-label', 'Review media viewer')
        dialog.innerHTML = `
          <div class="sai-czkr94ut__lightbox-panel">
            <button class="sai-czkr94ut__lightbox-close" type="button" data-action="lightbox-close" aria-label="Close">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12" stroke-linecap="round"/></svg>
            </button>
            <div class="sai-czkr94ut__lightbox-media-side">
              <button class="sai-czkr94ut__lightbox-nav sai-czkr94ut__lightbox-nav--prev" type="button" data-action="lightbox-prev" aria-label="Previous" hidden>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M10 3L5 8L10 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </button>
              <div class="sai-czkr94ut__lightbox-content"></div>
              <button class="sai-czkr94ut__lightbox-nav sai-czkr94ut__lightbox-nav--next" type="button" data-action="lightbox-next" aria-label="Next" hidden>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M6 3L11 8L6 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </button>
            </div>
            <div class="sai-czkr94ut__lightbox-review-side" data-slot="lightbox-review"></div>
          </div>
        `
        this.appendChild(dialog)
        this._lightbox = dialog
        this._lightboxItems = []
        this._lightboxIndex = 0

        this.addEventListener('click', (e) => {
          const thumb = e.target.closest('[data-action="media-open"]')
          if (!thumb) return

          const readItem = (t) => ({
            url: t.getAttribute('data-media-url'),
            type: t.getAttribute('data-media-type'),
            rating: Number(t.getAttribute('data-review-rating')) || 0,
            author: t.getAttribute('data-review-author') || '',
            title: t.getAttribute('data-review-title') || '',
            body: t.getAttribute('data-review-body') || '',
            date: t.getAttribute('data-review-date') || '',
            verified: t.getAttribute('data-review-verified') === 'verified_buyer',
          })

          const isGallery = !!thumb.closest('[data-slot="gallery"]')
          if (isGallery) {
            this._lightboxItems = [readItem(thumb)]
            this._lightboxIndex = 0
          } else {
            const review = thumb.closest('.sai-czkr94ut__review')
            if (!review) return
            const thumbs = Array.from(review.querySelectorAll('[data-action="media-open"]'))
            this._lightboxItems = thumbs.map(readItem)
            this._lightboxIndex = thumbs.indexOf(thumb)
          }
          this._showLightboxSlide()
          dialog.showModal()
          document.body.style.overflow = 'hidden'
          this._track(`${FEATURE_SLUG}:media_view`, { media_index: this._lightboxIndex })
        })

        const closeLightbox = () => {
          dialog.classList.add('sai-czkr94ut__lightbox--closing')
          setTimeout(() => {
            dialog.classList.remove('sai-czkr94ut__lightbox--closing')
            dialog.close()
            document.body.style.overflow = ''
          }, 250)
        }

        dialog.addEventListener('click', (e) => {
          const action = e.target.closest('[data-action]')?.getAttribute('data-action')
          if (action === 'lightbox-close') {
            closeLightbox()
            return
          }
          if (action === 'lightbox-prev') {
            this._lightboxIndex = Math.max(0, this._lightboxIndex - 1)
            this._showLightboxSlide()
            return
          }
          if (action === 'lightbox-next') {
            this._lightboxIndex = Math.min(this._lightboxItems.length - 1, this._lightboxIndex + 1)
            this._showLightboxSlide()
            return
          }
          if (e.target === dialog) closeLightbox()
        })
      }

      _showLightboxSlide() {
        const item = this._lightboxItems[this._lightboxIndex]
        if (!item) return
        const content = this._lightbox.querySelector('.sai-czkr94ut__lightbox-content')
        if (item.type === 'video') {
          content.innerHTML = `<video class="sai-czkr94ut__lightbox-media" src="${item.url}" controls autoplay playsinline></video>`
        } else {
          content.innerHTML = `<img class="sai-czkr94ut__lightbox-media" src="${item.url}" alt="" onerror="this.style.display='none'">`
        }

        const reviewSide = this._lightbox.querySelector('[data-slot="lightbox-review"]')
        if (reviewSide && (item.author || item.title || item.body)) {
          const rating = Math.round(item.rating || 0)
          const date = formatDate(item.date)
          let html = `<div class="sai-czkr94ut__lightbox-review-stars">${starsSvg(rating)}</div>`
          if (date) html += `<time class="sai-czkr94ut__lightbox-review-date">${date}</time>`
          html += `<div class="sai-czkr94ut__lightbox-review-meta">`
          html += `<span class="sai-czkr94ut__review-author">${item.author}</span>`
          if (item.verified)
            html += `<span class="sai-czkr94ut__badge sai-czkr94ut__badge--verified"><svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg> Verified</span>`
          html += '</div>'
          if (item.title)
            html += `<h3 class="sai-czkr94ut__lightbox-review-title">${item.title}</h3>`
          if (item.body)
            html += `<div class="sai-czkr94ut__lightbox-review-body">${item.body}</div>`
          reviewSide.innerHTML = html
          reviewSide.hidden = false
        } else if (reviewSide) {
          reviewSide.hidden = true
        }

        const prev = this._lightbox.querySelector('[data-action="lightbox-prev"]')
        const next = this._lightbox.querySelector('[data-action="lightbox-next"]')
        if (prev) prev.hidden = this._lightboxIndex <= 0
        if (next) next.hidden = this._lightboxIndex >= this._lightboxItems.length - 1
      }

      _initVoting() {
        if (this._voteBound) return
        this._voteBound = true
        this.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-action="vote"]')
          if (!btn) return
          let reviewId = btn.getAttribute('data-review-id')
          if (!reviewId) return
          const voteType = btn.getAttribute('data-vote-type')
          if (!voteType) return
          // SSR renders metaobject handle (spectrum-review-{uuid}); extract UUID
          if (reviewId.startsWith('spectrum-review-')) {
            reviewId = reviewId.replace('spectrum-review-', '')
          }
          this._vote(reviewId, voteType, btn)
        })
      }

      async _vote(reviewId, voteType, btn) {
        this._ensureProxyBase()
        const sdk = window.Spectrum?.reviews
        if (!sdk) return
        btn.disabled = true
        btn.style.opacity = '0.5'
        try {
          const result = await sdk.voteOnReview(reviewId, voteType, {
            productHandle: this._getProductHandle(),
          })
          btn.disabled = false
          btn.style.opacity = ''
          if (!result.ok) return
          const voteData = result.data?.data || result.data

          const card = btn.closest('.sai-czkr94ut__review')
          if (!card) return

          const voteBtns = card.querySelectorAll('[data-action="vote"]')
          for (const vb of voteBtns) {
            const type = vb.getAttribute('data-vote-type')
            const count = voteData?.voteCounts?.[type] || 0
            vb.classList.toggle(
              'sai-czkr94ut__vote-btn--active',
              voteData?.action !== 'removed' && type === voteType,
            )
            const countEl = vb.querySelector('.sai-czkr94ut__vote-count')
            if (countEl) {
              countEl.textContent = count
            }
          }

          this._track(`${FEATURE_SLUG}:vote`, {
            review_id: reviewId,
            vote_type: voteType,
            action: voteData?.action,
          })
        } catch (_) {
          btn.disabled = false
          btn.style.opacity = ''
          /* vote failure is silent */
        }
      }

      applyVariant(presentation) {
        if (!presentation) return
        if (typeof presentation.heading === 'string') {
          const h = this.querySelector('.sai-czkr94ut__heading')
          if (h) h.textContent = presentation.heading
        }
        if (typeof presentation.write_review_label === 'string') {
          const link = this.querySelector('[data-action="write-review"]')
          if (link) link.textContent = presentation.write_review_label
        }
        if (typeof presentation.write_review_url === 'string') {
          const link = this.querySelector('[data-action="write-review"]')
          if (link) {
            // The destination "Write a Review" page identifies the product
            // via a ?product=<handle> query param. The Liquid SSR builds the
            // href with that suffix already attached; the variant carries
            // only the base URL the merchant configured. Re-attach the
            // product handle here so an experience variant swapping the URL
            // at runtime can't accidentally strip the param.
            const base = presentation.write_review_url
            const handle = this._getProductHandle()
            const sep = base.indexOf('?') >= 0 ? '&' : '?'
            link.href = handle ? `${base}${sep}product=${encodeURIComponent(handle)}` : base
          }
        }
        if (typeof presentation.empty_state_text === 'string') {
          this.setAttribute('data-empty-text', presentation.empty_state_text)
          const empty = this.querySelector('.sai-czkr94ut__empty')
          if (empty) empty.textContent = presentation.empty_state_text
        }
      }
    }
    customElements.define(TAG, SaiReviews)
  }

  if (typeof globalThis !== 'undefined' && globalThis.__SAI_TEST_HARNESS__ === true) {
    globalThis.__saiCzkr94ut = {
      buildReviewCard,
      buildShimmer,
      starsSvg,
      formatDate,
      safeTrack,
    }
  }

  function bindAllContainers() {
    const api = window.__spectrumAi?.snippet
    const containers = document.querySelectorAll(
      `[data-spectrum-instance-id][data-spectrum-snippet-id="${SNIPPET_ID}"]`,
    )

    if (!api?.bind) return

    for (const node of containers) {
      const handles = api.bind(node, ({ variant }) => {
        if (!variant?.content) return
        const root = node.querySelector(TAG)
        if (root && typeof root.applyVariant === 'function') {
          root.applyVariant(variant.content)
        }
      })
      const root = node.querySelector(TAG)
      if (root && handles?.track && typeof root.setAnalytics === 'function') {
        root.setAnalytics(handles.track, handles.emit)
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindAllContainers, { once: true })
  } else {
    bindAllContainers()
  }
})()
